/**
 * Чек-лист задач для печати.
 *
 * Раньше кнопка звала `window.print()` — то есть печатала саму страницу.
 * На бумаге это бессмысленно: тёмный фон, навигация, свёрнутые раскрытия,
 * графики шириной в экран и десять этажей, из которых исполнителю нужен
 * один. Печать страницы отвечает на вопрос «как выглядел отчёт», а от
 * листа нужен ответ на «что делать и кто это сделал».
 *
 * Поэтому здесь собирается отдельный документ: только задачи, только то,
 * что нельзя восстановить по памяти (кампании поимённо, срок), и место,
 * куда вписывают исполнителя и отметку о выполнении. Форма листа —
 * договорённость: его подписывают и по нему принимают работу.
 *
 * Документ печатается из отдельного окна, а не из текущей страницы. Печать
 * через `@media print` на самой странице означала бы, что вёрстка отчёта и
 * вёрстка бумаги живут в одних и тех же правилах и ломают друг друга при
 * каждой правке.
 *
 * @format
 */

import {DAYS, pluralize} from './helpers';
import {stripTypography} from './typograph';
import type {Task, TaskPriority} from './types';

/** Срок в днях по приоритету. Держится в одном месте с разметкой секции. */
export const PRIORITY_DAYS: Record<TaskPriority, number> = {P1: 3, P2: 7, P3: 14};

/**
 * Подпись приоритета. Словами, а не кодом: на бумаге «P1» ничего не
 * значит для того, кто отчёта не читал, а лист уходит исполнителю
 * отдельно от него.
 */
export const PRIORITY_LABEL: Record<TaskPriority, string> = {
  P1: 'Приоритет 1',
  P2: 'Приоритет 2',
  P3: 'Приоритет 3',
};

export interface ChecklistMeta {
  /** Проект так, как его знает клиент. Пусто — идентификатор не нашёлся. */
  companyName?: string | null;
  /** Рекламная система: «Яндекс Директ». */
  adSystem: string;
  /** Период наблюдения одной строкой. */
  period: string;
  /** Дата съёма данных словами. */
  dataDate: string;
  /** Дата следующей проверки, если она известна. */
  nextReportDate?: string | null;
}

/**
 * Экранирование обязательно: в лист попадают имена кампаний из кабинета, а
 * там встречается что угодно — включая угловые скобки и амперсанды.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Текст на бумаге чистится от служебных символов — там они не нужны. */
function plain(value: string | null | undefined): string {
  return value ? escapeHtml(stripTypography(value)) : '';
}

/** Имя проекта на бумаге: без него лист не привязан ни к какому кабинету. */
function projectName(meta: ChecklistMeta): string {
  return plain(meta.companyName) || 'без названия';
}

function taskHtml(task: Task, index: number): string {
  const named = (task.campaigns ?? []).filter(campaign => campaign.name);
  const campaigns = named.length
    ? `<div class="c"><span class="cl">Кампании:</span> ${named
        .map(campaign => `<span class="cn">${plain(campaign.name ?? '')}</span>`)
        .join('')}</div>`
    : '';

  return `<li class="t">
  <div class="box"></div>
  <div class="body">
    <div class="head">
      <span class="p p${task.priority}">${PRIORITY_LABEL[task.priority]}</span>
      <span class="due">срок ${plain(pluralize(PRIORITY_DAYS[task.priority], DAYS))}</span>
      <span class="n">${index + 1}</span>
    </div>
    <div class="title">${plain(task.title)}</div>
    <div class="desc">${plain(task.description)}</div>
    ${campaigns}
    <div class="sign">
      <span class="field"><span class="lbl">Исполнитель</span></span>
      <span class="field"><span class="lbl">Выполнено</span></span>
    </div>
  </div>
</li>`;
}

/**
 * Знак AdSensor контурами.
 *
 * Файл автономен: он уезжает в письмо и в папку загрузок, где внешних
 * ссылок на наши ассеты уже нет. Поэтому знак вшит разметкой, а не
 * картинкой по адресу.
 *
 * Контуры сняты с `LogoAssemble` — это дубликат, и он честно им остаётся
 * до тех пор, пока дизайн-система не начнёт отдавать их данными: React-
 * компонент в строку HTML не превратить.
 */
const LOGO_SVG =
  '<svg class="logo" viewBox="0 0 113 30" width="113" height="30" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="AdSensor"><path d="M25.2422 12.043C24.8985 11.5586 24.4297 11.168 23.836 10.8711C23.2422 10.5742 22.5312 10.4258 21.7032 10.4258C20.1328 10.4258 18.8672 11.0078 17.9062 12.1719C16.9532 13.3359 16.4766 14.8242 16.4766 16.6367V17.0117C16.4766 18.8242 16.9493 20.3164 17.8946 21.4883C18.8398 22.6523 20.1133 23.2344 21.7148 23.2344C22.543 23.2344 23.2696 23.0625 23.8946 22.7188C24.5273 22.375 24.9883 21.9531 25.2773 21.4531H25.3477L25.4297 23H27.8438V5.02344H25.2422V12.043ZM25.2305 19.2031C25.0038 19.7266 24.6367 20.1875 24.1288 20.5859C23.6288 20.9766 23.0157 21.1719 22.2891 21.1719C21.2188 21.1719 20.4258 20.7812 19.9102 20C19.4023 19.2188 19.1485 18.2422 19.1485 17.0703V16.5898C19.1485 15.418 19.4062 14.4414 19.9219 13.6602C20.4375 12.8789 21.2305 12.4883 22.3008 12.4883C23.0352 12.4883 23.6485 12.6875 24.1407 13.0859C24.6407 13.4766 25.0038 13.9297 25.2305 14.4453V19.2031Z" fill="#111"/><path d="M6.48047 5.83203L0 23H2.61328L4.15808 18.7812H11.2286L12.7617 23H15.6211L9.09375 5.83203H6.48047ZM4.93909 16.6484L7.23047 10.3906C7.32422 10.1328 7.40234 9.88281 7.46484 9.64062C7.53516 9.39062 7.59766 9.14062 7.65234 8.89062H7.75781C7.8125 9.14062 7.87109 9.38672 7.93359 9.62891C8.00391 9.87109 8.08594 10.125 8.17969 10.3906L10.4536 16.6484H4.93909Z" fill="#111"/><path d="M37.8398 13.1445C36.0663 12.6523 34.9062 12.2148 34.3593 11.832C33.8124 11.4414 33.539 10.8984 33.539 10.2031C33.539 9.46094 33.8007 8.87109 34.3242 8.43359C34.8554 7.99609 35.6757 7.77734 36.7851 7.77734C37.9023 7.77734 38.7421 8.03906 39.3046 8.5625C39.8671 9.07812 40.1484 9.75781 40.1484 10.6016V10.7773H42.8437V10.5898C42.8437 9.11328 42.2929 7.91016 41.1913 6.98047C40.0976 6.05078 38.6288 5.58594 36.7851 5.58594C34.9413 5.58594 33.4882 6.03125 32.4257 6.92188C31.3632 7.8125 30.832 8.94922 30.832 10.332C30.832 11.6836 31.246 12.7344 32.0742 13.4844C32.9023 14.2344 34.2343 14.8633 36.0702 15.3711C37.8202 15.8633 38.9921 16.3281 39.5859 16.7656C40.1796 17.1953 40.4765 17.7812 40.4765 18.5234C40.4765 19.3125 40.1679 19.9492 39.5507 20.4336C38.9335 20.918 38.0507 21.1602 36.9023 21.1602C35.746 21.1602 34.8437 20.918 34.1952 20.4336C33.5546 19.9492 33.2343 19.2266 33.2343 18.2656V18.0781H30.5156V18.2773C30.5156 19.8711 31.1015 21.1172 32.2734 22.0156C33.4452 22.9062 34.9882 23.3516 36.9023 23.3516C38.8163 23.3516 40.3398 22.9023 41.4726 22.0039C42.6132 21.1055 43.1835 19.918 43.1835 18.4414C43.1757 16.9883 42.7343 15.8672 41.8593 15.0781C40.9843 14.2891 39.6445 13.6445 37.8398 13.1445Z" fill="#111"/><path d="M50.7657 10.3086C49.0469 10.3086 47.6562 10.8945 46.5938 12.0664C45.5312 13.2383 45 14.7617 45 16.6367V17.0117C45 18.8945 45.5273 20.4219 46.5821 21.5938C47.6446 22.7656 49.1485 23.3516 51.0938 23.3516C52.2969 23.3516 53.3243 23.1016 54.1758 22.6016C55.0352 22.1016 55.6602 21.5312 56.0508 20.8906L54.293 19.6133C53.9571 20.0664 53.5196 20.4609 52.9805 20.7969C52.4493 21.1328 51.8125 21.3008 51.0703 21.3008C49.9141 21.3008 49.0508 20.9023 48.4805 20.1055C47.9767 19.3848 47.7113 18.4919 47.6587 17.4453H56.1797V16.4258C56.1797 14.543 55.7071 13.0547 54.7617 11.9609C53.8163 10.8594 52.4844 10.3086 50.7657 10.3086ZM53.5782 15.5586H47.7126C47.8229 14.7815 48.0584 14.1057 48.4336 13.543C48.9648 12.7383 49.7617 12.3359 50.8243 12.3359C51.7071 12.3359 52.3867 12.625 52.8633 13.2031C53.3398 13.7734 53.5782 14.4688 53.5782 15.2891V15.5586Z" fill="#111"/><path d="M89.7422 10.3086C87.9375 10.3086 86.4844 10.8945 85.3828 12.0664C84.2891 13.2305 83.7422 14.7539 83.7422 16.6367V17.0117C83.7422 18.9023 84.2891 20.4336 85.3828 21.6055C86.4844 22.7695 87.9375 23.3516 89.7422 23.3516C91.5469 23.3516 92.9961 22.7695 94.0898 21.6055C95.1836 20.4336 95.7305 18.9023 95.7305 17.0117V16.6367C95.7305 14.7539 95.1836 13.2305 94.0898 12.0664C92.9961 10.8945 91.5469 10.3086 89.7422 10.3086ZM93.0586 17.082C93.0586 18.3164 92.7773 19.3242 92.2148 20.1055C91.6523 20.8867 90.8282 21.2773 89.7422 21.2773C88.6485 21.2773 87.8203 20.8867 87.2578 20.1055C86.6953 19.3242 86.4141 18.3164 86.4141 17.082V16.5781C86.4141 15.3438 86.6953 14.3359 87.2578 13.5547C87.8282 12.7656 88.6562 12.3711 89.7422 12.3711C90.8203 12.3711 91.6407 12.7656 92.2032 13.5547C92.7735 14.3359 93.0586 15.3438 93.0586 16.5781V17.082Z" fill="#111"/><path d="M104.8 10.3672C104.605 10.3281 104.417 10.3086 104.238 10.3086C103.48 10.3086 102.824 10.5078 102.269 10.9062C101.714 11.2969 101.266 11.7539 100.921 12.2773H100.875L100.781 10.6602H98.3555V23H100.968V14.6797C101.227 14.1172 101.625 13.6523 102.164 13.2852C102.71 12.9102 103.359 12.7227 104.109 12.7227C104.328 12.7227 104.527 12.7382 104.707 12.7578C104.886 12.7772 105.058 12.7969 105.222 12.8281V10.4727C105.136 10.4414 104.996 10.4062 104.8 10.3672Z" fill="#111"/><path d="M77.7071 15.6523C76.3633 15.3477 75.4805 15.0703 75.0586 14.8203C74.6367 14.5625 74.4258 14.2188 74.4258 13.7891C74.4258 13.3594 74.6055 13.0039 74.9648 12.7227C75.3243 12.4414 75.8946 12.3008 76.6758 12.3008C77.4648 12.3008 78.0469 12.457 78.4219 12.7695C78.7969 13.082 78.9844 13.5117 78.9844 14.0586V14.1992H81.5743V14.1055C81.5743 12.9414 81.125 12.0195 80.2266 11.3398C79.336 10.6523 78.1562 10.3086 76.6875 10.3086C75.211 10.3086 74.0352 10.6484 73.1602 11.3281C72.2852 12 71.8477 12.8711 71.8477 13.9414C71.8477 14.9492 72.2188 15.7461 72.961 16.332C73.7032 16.9102 74.7578 17.3555 76.125 17.668C77.461 17.9648 78.3203 18.2461 78.7032 18.5117C79.0938 18.7773 79.2891 19.1641 79.2891 19.6719C79.2891 20.1719 79.0663 20.582 78.6211 20.9023C78.1758 21.2148 77.5547 21.3711 76.7578 21.3711C75.961 21.3711 75.3203 21.207 74.836 20.8789C74.3594 20.5508 74.1211 20.0508 74.1211 19.3789V19.2793H71.5312V19.3555C71.5312 20.5898 71.9805 21.5664 72.8788 22.2852C73.7773 22.9961 75.0703 23.3516 76.7578 23.3516C78.4532 23.3516 79.7305 23 80.5898 22.2969C81.4493 21.5859 81.8788 20.6562 81.8788 19.5078C81.8788 18.4219 81.5235 17.5859 80.8125 17C80.1094 16.4062 79.0743 15.957 77.7071 15.6523Z" fill="#111"/><path d="M64.957 10.4141C64.1835 10.4141 63.4765 10.5742 62.8359 10.8945C62.1952 11.2148 61.6718 11.6406 61.2656 12.1719H61.207L61.1132 10.6602H58.6992V23H61.3124V14.4219C61.5859 13.8516 61.9726 13.3945 62.4726 13.0508C62.9726 12.707 63.5351 12.5352 64.1601 12.5352C64.9101 12.5352 65.4843 12.7305 65.8827 13.1211C66.289 13.5039 66.4921 14.1523 66.4921 15.0664V23H69.1054V15.1133C69.1054 13.4102 68.7265 12.2031 67.9687 11.4922C67.2109 10.7734 66.207 10.4141 64.957 10.4141Z" fill="#111"/><path d="M112.105 0H109.105V30H112.105V0Z" fill="#FF0000"/></svg>';

/** Сроки одной строкой — из тех же двух таблиц, что и подписи задач. */
function dueLine(): string {
  return (['P1', 'P2', 'P3'] as TaskPriority[])
    .map(key => `${PRIORITY_LABEL[key]} — ${pluralize(PRIORITY_DAYS[key], DAYS)}`)
    .join(', ');
}

/**
 * Собирает документ целиком.
 *
 * Стили инлайновые и печатные: белый фон, чёрный текст, никаких токенов
 * тёмной темы. Разрывы страниц запрещены внутри задачи — половина задачи
 * на одном листе, половина на другом означает, что её не сделают.
 */
export function checklistHtml(tasks: Task[], meta: ChecklistMeta): string {
  const checkpoint = meta.nextReportDate
    ? `<div class="cp">Проверка выполнения: ${plain(meta.nextReportDate)}</div>`
    : '';

  return `<!doctype html>
<html lang="ru"><head><meta charset="utf-8">
<title>Чек-лист задач — ${projectName(meta)}</title>
<style>
  @page { size: A4; margin: 14mm 14mm 16mm; }
  * { box-sizing: border-box; }
  /* На экране документ живёт сам по себе: поля задаём телу, ширину
     держим в размер листа, чтобы вкладка не растягивала строки. */
  body { margin: 0 auto; padding: 12mm 14mm; max-width: 210mm;
         font: 11pt/1.45 -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111; }
  /* На бумаге поля даёт @page — вторые сверху были бы двойными. */
  @media print { body { padding: 0; max-width: none; } }
  .brand { margin-bottom: 6mm; }
  .logo { height: 8mm; width: auto; display: block; }
  /* Вес всюду 400: иерархию держит кегль. У h1 вес браузерный, поэтому
     его приходится гасить явно. */
  h1 { font-size: 21pt; font-weight: 400; margin: 0 0 2mm; }
  .meta { font-size: 9.5pt; color: #444; margin-bottom: 1mm; }
  .cp { font-size: 11pt; color: #111; margin-top: 2mm; }
  .rule { border: 0; border-top: 1pt solid #111; margin: 4mm 0 5mm; }
  .lead { font-size: 9.5pt; color: #444; margin-bottom: 5mm; }
  ol { list-style: none; margin: 0; padding: 0; }
  .t { display: flex; gap: 4mm; padding: 3.5mm 0; border-bottom: 0.5pt solid #ccc;
       page-break-inside: avoid; break-inside: avoid; }
  .box { flex: none; width: 5mm; height: 5mm; border: 1pt solid #111; margin-top: 1mm; }
  .body { flex: 1 1 auto; min-width: 0; }
  .head { font-size: 8.5pt; color: #444; margin-bottom: 1mm; }
  .p { color: #111; }
  .pP1 { color: #b3261e; } .pP2 { color: #8a5a00; } .pP3 { color: #444; }
  .due::before { content: " · "; }
  .n { float: right; color: #888; }
  /* Единственное отступление от 400: названия задач держат 500. Кегль
     их уже отделяет, но в списке из десяти пунктов взгляд цепляется за
     насыщенность быстрее, чем за размер. */
  .title { font-size: 14pt; font-weight: 500; margin-bottom: 1mm; }
  .desc { font-size: 10pt; color: #222; }
  .c { font-size: 9pt; color: #444; margin-top: 1.5mm; }
  .cl { color: #888; }
  .cn { display: inline-block; border: 0.5pt solid #bbb; border-radius: 2pt;
        padding: 0.3mm 1.4mm; margin: 0.6mm 1mm 0 0; word-break: break-all; }
  .sign { margin-top: 3mm; display: flex; gap: 8mm; }
  .field { flex: 1 1 0; border-bottom: 0.5pt solid #999; padding-top: 6mm; }
  .lbl { font-size: 7.5pt; color: #999; }
  .foot { margin-top: 6mm; font-size: 8pt; color: #888; }
</style></head>
<body>
  <div class="brand">${LOGO_SVG}</div>
  <h1>Чек-лист задач по рекламному кабинету</h1>
  <div class="meta">Проект «${projectName(meta)}» · ${plain(meta.adSystem)} · период ${plain(meta.period)} · данные на ${plain(meta.dataDate)}</div>
  ${checkpoint}
  <hr class="rule">
  <div class="lead">Сроки считаются от даты передачи задач: ${dueLine()}. Отметьте выполненное и впишите исполнителя.</div>
  <ol>${tasks.map(taskHtml).join('')}</ol>
  <div class="foot">Сформировано AdSensor по данным кабинета на ${plain(meta.dataDate)}.</div>
</body></html>`;
}

/**
 * Имя файла: по нему лист находят в папке загрузок через месяц.
 *
 * Косые, двоеточия и кавычки в именах файлов запрещены или неудобны, а имя
 * проекта приходит из кабинета и содержит что угодно.
 */
function fileName(meta: ChecklistMeta): string {
  const safe = (projectName(meta) || 'проект').replace(/[\\/:*?"<>|]+/g, ' ').trim();
  return `Чек-лист задач — ${safe} — ${meta.dataDate}.html`;
}

/**
 * Отдаёт чек-лист файлом: скачивает и открывает в соседней вкладке.
 *
 * Не печать. Диалог печати навязывает решение сразу и не оставляет
 * документа: закрыл — и восстановить нечего. Файл живёт в загрузках,
 * открывается заново, уходит в письмо и печатается когда удобно.
 *
 * Не `window.open('')` с записью в документ: с флагом `noopener` браузер
 * возвращает `null`, и окно остаётся пустым навсегда — писать в него уже
 * некуда. Blob-ссылка от этого свободна: вкладка получает готовый
 * документ, а не ссылку на наш контекст.
 *
 * Возвращает `false`, если вкладку заблокировали. Файл при этом всё равно
 * скачан — скачивание не зависит от разрешения на всплывающие окна.
 */
export function exportChecklist(tasks: Task[], meta: ChecklistMeta): boolean {
  const blob = new Blob([checklistHtml(tasks, meta)], {type: 'text/html;charset=utf-8'});
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName(meta);
  document.body.appendChild(link);
  link.click();
  link.remove();

  const tab = window.open(url, '_blank');

  // Ссылку отзываем с задержкой: вкладке нужно время, чтобы её загрузить,
  // а немедленный revoke оставил бы её с пустой страницей — ровно тем, от
  // чего мы и уходим.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);

  return Boolean(tab);
}
